import React, { useState, useEffect } from "react";
import dayjs from "dayjs";

import { Card, Button, Grid, Stack } from "@mui/material";
import TextField from "@mui/material/TextField";

import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

import { SearchIcon as SearchRoundedIcon } from "components/icons";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import * as AppConstants from "AppConstants";
import { loadActivitiesFromBackendForMember } from "app/services/member.service";
import { loadClientProjectsByClient } from "app/services/client-project.service";
import { loadOwnActivitiesFromBackendForManager } from "app/services/manager.service";
import {
  checkUserHasMemberRole,
  getCurrentMemberId,
  readSessionCurrentMember,
} from "app/services/app.state.service";
import { deleteActivity } from "app/services/activity.stats.service";
import { isEmpty } from "utils/validationUtil";
import { getCurrentMonthStartDate } from "utils/dateUtil";
import { handleAxiosError } from "app/services/app.error.handler";
import { AddActivityForm } from "app/components/team-activities/AddActivityForm";
import { ActivityDataGridBaseEditable } from "app/components/team-activities/ActivityDataGridBaseEditable";
import { ConfirmDialog } from "app/components/ConfirmDialog";
import { EditActivityDialog } from "./EditActivityDialog";
import { ActivityDataGridBase } from "./ActivityDataGridBase";

const MemberActivityDataGridView = ({selectedMember = null}) => {
  const [memberId, setMemberId] = React.useState(0); // default memberId = 10 which is for "Latesh"
  const [fromDate, setFromDate] = React.useState(dayjs(getCurrentMonthStartDate()));
  const [toDate, setToDate] = React.useState(dayjs(new Date()));
  const [activitiesData, setActivitiesData] = React.useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDataObject, setEditDataObject] = useState(null);
  const [projects, setProjects] = useState([]);

  useEffect(() => {
    if (!checkUserHasMemberRole() && !selectedMember) return;

    var member = selectedMember ? selectedMember : readSessionCurrentMember();

    if (isEmpty(member)) return;
      setMemberId(member.id);

      searchClickHandler();
  }, [selectedMember, fromDate, toDate]);

  // An activity stores only its project id, so the client's projects (deactivated ones
  // included, else historical rows would read blank) resolve the Project Code column.
  useEffect(() => {
    const clientId = selectedMember?.client?.id;
    if (!clientId) {
      setProjects([]);
      return undefined;
    }
    let cancelled = false;
    loadClientProjectsByClient(clientId)
      .then((response) => {
        if (!cancelled) setProjects(Array.isArray(response?.data) ? response.data : []);
      })
      .catch(() => {
        // A failed lookup only blanks the codes, so it must not block the activities below it.
        if (!cancelled) setProjects([]);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedMember]);

  const searchClickHandler = async () => {

    if(!selectedMember)
    {
      Promise.resolve(getCurrentMemberId()).then((mId) => {
        if (isEmpty(mId)) return;
        setMemberId(mId);
        getActivitiesFromBackend(mId, fromDate, toDate);
      });
    }
    else{
      setMemberId(selectedMember.id);
      getActivitiesFromBackend(selectedMember.id, fromDate, toDate);
    }
  };

  const getActivitiesFromBackend = async (ownerId, fromDate, toDate) => {

    // A manager row stands for a manager record, whose activities are owned by manager_id
    // rather than member_id — and only their own, not their team's.
    const loadActivities = selectedMember?.isManagerRecord
      ? loadOwnActivitiesFromBackendForManager
      : loadActivitiesFromBackendForMember;

    await loadActivities(ownerId, fromDate, toDate)
      .then((response) => {
        setActivitiesData(response.data);
      })
      .catch((error) => {
        setActivitiesData([]); // Set the empty data in activity table
        handleAxiosError(error);
      });
  };

  if (!checkUserHasMemberRole() && !selectedMember) {
    return <div>You do not have access to this functionality.</div>;
  }

  const deleteActivityFromBackend = async () => {
    await deleteActivity(deleteId)
      .then((response) => {
        searchClickHandler();
      })
      .catch((error) => {
        handleAxiosError(error);
      });
  };

  const deleteActivityHandler = () => {
    deleteActivityFromBackend();
  };

  const ConfirmDeleteActivity = (id) => {
    setConfirmOpen(true);
    setDeleteId(id);
  };

  const handleEditOpen = (dataObj) => {
    setEditOpen(true);
    setEditDataObject(dataObj);
  };

  return (
    <div className="data-grid-container" style={{ width: "100%" }}>
      <Grid container spacing={6}>
        {!selectedMember && <AddActivityForm ownerId={memberId} refreshActivitiesDataHandler={searchClickHandler} />}

        <Grid size={12}>
          <Card>
            <MDBox
              mx={2}
              mt={-3}
              py={1}
              px={2}
              variant="gradient"
              bgColor="info"
              borderRadius="lg"
              coloredShadow="info"
            >
              <MDTypography variant="h6" color="white">
                {selectedMember && `${selectedMember.name}'s Activities${selectedMember.client?.name ? ` — Client: ${selectedMember.client.name}` : ''}`}
                {!selectedMember && `My Activities`}
              </MDTypography>
            </MDBox>
            <MDBox mt={3} p={2}>
              <Stack direction="row" spacing={2}>
                <MDBox>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      disableFuture
                      label="From"
                      value={fromDate}
                      onChange={(newFromDate) => {
                        setFromDate(newFromDate);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                      inputFormat={AppConstants.DATE_FORMAT_DISPLAY}
                    />
                  </LocalizationProvider>
                </MDBox>
                <MDBox>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      disableFuture
                      label="To"
                      value={toDate}
                      onChange={(newToDate) => {
                        setToDate(newToDate);
                      }}
                      renderInput={(params) => <TextField {...params} />}
                      inputFormat={AppConstants.DATE_FORMAT_DISPLAY}
                    />
                  </LocalizationProvider>
                </MDBox>
                <MDBox>
                  <Button
                    onClick={searchClickHandler}
                    sx={{ width: 140, m: 0 }}
                    variant="contained"
                    color="success"
                    endIcon={<SearchRoundedIcon />}
                  >
                    Search
                  </Button>
                </MDBox>
              </Stack>
            </MDBox>

            {selectedMember && <ActivityDataGridBase activitiesData={activitiesData} projects={projects} />}
            {!selectedMember && <ActivityDataGridBaseEditable
              activitiesData={activitiesData}
              refreshActivitiesDataHandler={searchClickHandler}
              confirmDialogHandler={ConfirmDeleteActivity}
              editDialogHandler={handleEditOpen}
              role={AppConstants.ROLE_EMPLOYEE_MEMBER}
            />}
          </Card>
        </Grid>
      </Grid>
      <ConfirmDialog
        title="Delete Activity?"
        open={confirmOpen}
        setOpen={setConfirmOpen}
        onConfirm={deleteActivityHandler}
        yesButtonLabel="Delete"
        noButtonLabel="Cancel"
      >
        Are you sure you want to delete this activity?
      </ConfirmDialog>
      <EditActivityDialog
        editOpen={editOpen}
        setEditOpen={setEditOpen}
        rowData={editDataObject}
        refreshActivitiesDataHandler={searchClickHandler}
      />
    </div>
  );
};

export default MemberActivityDataGridView;
